import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { filter, take, switchMap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { Chain } from 'src/app/classes/async-chain';
import { POSITIONS } from 'src/app/constants/positions';
import { UserFacade } from 'src/app/services/facades/user/user.facade';
import { State } from 'src/app/state/interfaces';
import { logoutSuccess } from 'src/app/state/actions/auth/auth.actions';
import { AuthEffects } from 'src/app/state/effects/auth.effects';

import { AuthFacade } from './facades/auth/auth.facade';
import { AppService } from './app.service';
import { FlowService as ManagerFlowService } from '../modules/manager/services/flow.service'
import { MixpanelService } from './mixpanel.service';
import { FirebaseAuthService } from './firebase/firebase-auth.service';
import branch from 'branch-sdk';
import { BrazeService } from '../modules/manager/services/braze.service';
import { FacilitiesService } from '../modules/manager/services/api/facilities.service';

@Injectable({ providedIn: 'root' })
export class FlowService {
  private _init = new Chain({ debug: !this.app.isProduction, id: 'init' })
    .add('async', {
      token: () =>
        this.firebaseAuth.authReady$.pipe(
          filter(ready => ready),
          take(1),
          switchMap(() => this.firebaseAuth.token$.pipe(take(1)))
        ).toPromise(),
    })
    .add('sync', '', ({ token }) =>
      this.authFacade.setToken(token),
    )
    .add('sync', 'profile', ({ token }) => {
      if(token){
        return this.userFacade.getProfile()
      }else{
        return null
      }
    })
    .add('sync', '', ({ profile }) => {
      if(profile['user'] && profile['user'].position === 'admin'){
        this._logout.execute();
      }
    })



  private _login = new Chain({ debug: !this.app.isProduction, id: 'login' })
    .add('sync', '', () => this.userFacade.getProfile())
    .add('sync', 'user', () => this.userFacade.state.user$.pipe(take(1)))
    .add('sync', 'position', ({ user }) => user.position)
    .add('sync', '', ({ position, user }) => {
      if (!POSITIONS.ADMIN.includes(position)) {
        const activeFacilities = JSON.parse(localStorage.getItem('activeFacilities') || '[]');
        const activeFacilityId = activeFacilities.find((e) => e.userId === user._id)?.activeFacilityId;

        if (activeFacilityId) {
          this.facilitiesService
            .fetchFacility(activeFacilityId)
            .pipe(take(1))
            .subscribe((facility) => {
              if (facility?.billingType === 'saas') {
                this.router.navigateByUrl('manager/dashboard');
              } else {
                this.router.navigateByUrl('manager');
              }
            });
        } else {
          this.router.navigateByUrl('manager');
        }
        this.mixpanel.for('fm').track('LOGIN');
      } else {
        this._logout.execute();
      }
    });


  private _logout = new Chain({ debug: !this.app.isProduction, id: 'logout' })
    .add('async', {
      mixpanelOptOut: () => this.mixpanel.onLogout()
    })
    .add('sync', '', () => this.store.dispatch(logoutSuccess()))
    .add('sync', '', () => this.router.navigateByUrl('/'));

  constructor(
    private app: AppService,
    private store: Store<State>,
    private authEffects: AuthEffects,
    private authFacade: AuthFacade,
    private userFacade: UserFacade,
    private router: Router,
    private managerFlowService: ManagerFlowService,
    private mixpanel: MixpanelService,
    private brazeService: BrazeService,
    private facilitiesService: FacilitiesService,
    private firebaseAuth: FirebaseAuthService,
  ) {
    app.expose('services', 'flow', this);
  }

  register() {
    this._init.execute();
    this._login.startOn(this.authEffects.loginSuccess$);
    this._logout.startOn(this.authEffects.logout$);

    // Keep NgRx token in sync with Firebase token changes (proactive refresh).
    // Wait for authReady$ to avoid dispatching the BehaviorSubject's initial
    // null before Firebase has had a chance to refresh an expired token.
    this.firebaseAuth.authReady$.pipe(
      filter(ready => ready),
      take(1),
      switchMap(() => this.firebaseAuth.token$)
    ).subscribe((token) => {
      this.authFacade.setToken(token);
    });

    this.userFacade.effects.getProfile$.subscribe((p)=>{
      if(p['user'] && p['user'].position != 'admin'){
        this.managerFlowService.register();
        this.brazeService.initialize(true, p['user']._id);
        this.brazeService.setUserAttributes(p['user']);
      }
    });
    branch.init(environment.branchKey, function(err, data) {
      // console.log('branch.init', err, data);
    });
  }
}
